import {Alert, Box, Button} from "@mui/material";
import {Folio, FolioStatus} from "crqlar-react-common/dist/models/billing/folio.model";
import {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {ReservationGroup} from "crqlar-react-common/dist/models/reservation/reservationGroup.model";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {addMinutes} from "crqlar-react-common/dist/utils/date.util";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import DigitalSigningDetailsDialog from "../../../../digitalSigning/details/DigitalSigningDetailsDialog";
import DigitalSigningChip from "../../../../digitalSigning/share/DigitalSigningChip";
import DigitalSigningDialog from "../../../../digitalSigning/share/DigitalSigningDialog";
import {useDigitalSigning} from "../../../../digitalSigning/share/useDigitalSigning";
import {billablePersonIds} from "../folioCharge.util";
import FolioActionDialogs from "./FolioActionDialogs";
import {isBlankNewLine} from "./folioDraft.util";
import FolioFinalizeIssues from "./FolioFinalizeIssues";
import FolioFooter from "./FolioFooter";
import {FolioFormat} from "./folioFormat";
import FolioHeader, {FolioHeaderProps} from "./FolioHeader";
import {folioIssues, isTipLine} from "./folioLine.util";
import FolioLinesEditor from "./FolioLinesEditor";
import FolioPaymentSection from "./FolioPaymentSection";
import {useFolioActions} from "./useFolioActions";
import {useFolioPayment} from "./useFolioPayment";
import {FolioSession} from "./useFolioSession";

interface Props {
    /** Whether the dialog is open: the signing is followed only then. */
    open: boolean
    /** The dialog's header: the view renders it, with the signing after the folio's status. */
    header: FolioHeaderProps
    session: FolioSession
    reservation?: Reservation
    service?: Service
    /** The package the reservation is a member of: its folio bills one of the package's guests. */
    packageGroup?: ReservationGroup
    currency: string
    format: FolioFormat
    /** The printout of what is saved, in a new tab - the draft is saved first. */
    onPrint: () => void
}

/**
 * The folio view, shaped like the reservation dialog: the header - the signing's status
 * beside the folio's -, the lines across the full width with their totals - a finalized folio's
 * payment beside them -, and the footer with Print and the next step.
 */
const FolioEditorView = ({open, header, session, reservation, service, packageGroup, currency, format, onPrint}: Props) => {
    const {t} = useTranslation();
    const {folio, draft, setFolio, reportChange} = session;
    const [showIssues, setShowIssues] = useState(false);
    const actions = useFolioActions(session);
    const payment = useFolioPayment({
        folio,
        enabled: !session.readOnlyUser,
        onCharged: charged => {
            setFolio(charged);
            reportChange(charged);
        },
        onFailed: setFolio,
    });

    // Confirming the signing posted the tip and finalized the folio: on to the payment step
    const handleSigningConfirmed = (confirmed: Folio) => {
        setFolio(confirmed);
        session.refetchItems();
        reportChange(confirmed);
    };
    // Followed as long as the folio shows - the header's chip and the footer read it, whether its
    // dialog is open or not
    const digitalSigning = useDigitalSigning({
        folio,
        reservation,
        // A blank "Choose a service" line is never saved, so it doesn't block the link
        items: draft.ready ? draft.lines.map(line => line.item).filter(item => !isBlankNewLine(item)) : undefined,
        open,
        readOnly: session.readOnlyUser,
        beforeCreate: draft.persist,
        onConfirmed: handleSigningConfirmed,
        // The link bills someone else now: the folio as the backend has it, and every list reads again
        onBillToChanged: async () => {
            const {data} = await session.refetchFolio();
            if (data) reportChange(data);
        },
    });

    // Whom a finalized folio's charge can bill; a pick among several goes with the payment
    const personIds = folio ? billablePersonIds(folio, reservation, packageGroup) : [];
    // Tips already on the folio - tip lines, and lines of a tip service (e.g. one added by hand):
    // confirming adds the guest's as well, so the footer asks first
    const tipServiceIds = new Set(session.services.filter(candidate => candidate.tipService).map(candidate => candidate.id));
    const postedTips = session.visibleItems.filter(item => isTipLine(item) || (!!item.serviceId && tipServiceIds.has(item.serviceId)));

    const finalizeIssues = folioIssues(session.visibleItems, {vatRequired: payment.vatRequired});
    // A folio that can't change shows its stored total: that is what was charged
    const storedTotalCents = folio && folio.status !== FolioStatus.DRAFT ? folio.total?.amountCents : undefined;
    // The reservation's day: a line shows its date only when it's on another one
    const referenceDay = reservation?.expectedStartDate ?? draft.lines[0]?.item.timeSpanStart;
    // A new line starts at the reservation's time, with its duration
    const newLineStart = referenceDay ?? new Date();
    const newLine = {
        start: newLineStart,
        end: reservation?.expectedEndDate ?? addMinutes(newLineStart, service?.durationMinutes ?? 0),
    };

    // Finalize explains what is missing instead of finalizing it
    const handleFinalize = () => {
        draft.revealErrors();
        if (finalizeIssues.length) setShowIssues(true);
        else actions.request('finalize');
    };

    // The draft is saved first - a failed save stays, with its error above the lines, and nothing is
    // read -, then the folio, its lines and its signing are read again, then its payment
    const refresh = async () => {
        const read = await session.refresh();
        if (read) await payment.refresh();
        return read;
    };

    return (
        <>
            {/* Nothing to read again while the folio is still prepared, or read the first time */}
            <FolioHeader {...header} view={'folio'} onRefresh={folio || session.folioError ? refresh : undefined}>
                <DigitalSigningChip flow={digitalSigning} format={format}/>
            </FolioHeader>

            {/* Between the pinned header and footer, as tall as its content until the screen ends: then on
                a phone it scrolls as a whole, larger the lines do */}
            <Box sx={{flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', overflowY: {xs: 'auto', md: 'hidden'}}}>
                {session.folioError ? (
                    <Box sx={{p: 3}}>
                        <Alert
                            severity={'warning'}
                            action={(
                                <>
                                    <Button color={'inherit'} size={'small'} onClick={() => session.refetchFolio()}>{t('Retry')}</Button>
                                    <Button color={'inherit'} size={'small'} onClick={header.onShowAll}>{t('All folios')}</Button>
                                </>
                            )}
                        >
                            {t('The folio could not be loaded.')}
                        </Alert>
                    </Box>
                ) : (
                    <FolioLinesEditor
                        draft={draft}
                        editable={session.linesEditable}
                        currency={currency}
                        services={session.services}
                        reservationService={service}
                        vatRequired={payment.vatRequired}
                        hiddenIds={session.hiddenIds}
                        referenceDay={referenceDay}
                        newLine={newLine}
                        storedTotalCents={storedTotalCents}
                        loadError={session.linesError}
                        onRetry={() => session.refetchItems()}
                        payment={folio && folio.status !== FolioStatus.DRAFT && (
                            <FolioPaymentSection folio={folio} payment={payment} readOnlyUser={session.readOnlyUser} personIds={personIds}/>
                        )}
                        pendingTipCents={digitalSigning.pendingTipCents || undefined}
                    />
                )}
            </Box>

            {/* Read-only users get no actions: their footer prints, and shows what the guest signed */}
            {!session.folioError && (
                <FolioFooter
                    folio={folio}
                    readOnly={session.readOnlyUser}
                    saving={draft.saving}
                    payment={payment}
                    actions={actions}
                    digitalSigning={digitalSigning}
                    postedTips={postedTips}
                    format={format}
                    onFinalize={handleFinalize}
                    onPrint={onPrint}
                />
            )}

            {/* Finalize with issues: what is missing, instead of the confirmation */}
            <FolioFinalizeIssues
                open={showIssues}
                lines={session.visibleItems}
                issues={finalizeIssues}
                onClose={() => setShowIssues(false)}
                onContinue={() => {
                    setShowIssues(false);
                    actions.request('finalize');
                }}
            />
            <FolioActionDialogs actions={actions} folio={folio} guestLinkOpen={session.guestLinkOpen} format={format}/>
            <DigitalSigningDialog flow={digitalSigning}/>
            {/* What the guest signed, as a view of the folio: its arrow leads back, its X closes both */}
            {digitalSigning.detailsOpen && !!digitalSigning.latest && (
                <DigitalSigningDetailsDialog
                    id={digitalSigning.latest.id}
                    open
                    onBack={digitalSigning.hideDetails}
                    onClose={() => {
                        digitalSigning.hideDetails();
                        header.onClose();
                    }}
                />
            )}
        </>
    );
};

export default FolioEditorView;
